import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, getCallerAgentId, hasLiveApi } from '@/api/client'
import { agentNodeToAccount, type AgentNode } from '@/api/map/agent'
import { paiseToDisplay, parseCurrencyType } from '@/lib/currency'
import { toCreditTransferRequest } from '@/api/map/credit'
import { invalidateAfterCreditTransfer } from '@/api/hooks/credit/invalidateAfterCreditTransfer'
import * as api from '@/data/api'
import type { Account, Transfer } from '@/data/types'

interface CreditTransferResult {
  transferId: string
  targetId: string
  direction: 'give' | 'take'
  amount: string
  replayed: boolean
}

async function loadAccount(id: string): Promise<Account> {
  const node = await apiFetch<AgentNode>(`/v1/agents/${id}`)
  return agentNodeToAccount(node)
}

async function transferCreditLive(input: api.TransferInput): Promise<api.TransferResultRow> {
  const selfId = getCallerAgentId()!
  if (!(input.amount > 0)) throw new Error('Enter an amount greater than zero')

  const body = toCreditTransferRequest({
    direction: input.direction,
    amount: input.amount,
    remark: input.remark,
    currencyType: input.currencyType,
  })

  const result = await apiFetch<CreditTransferResult>(`/v1/agents/${input.accountId}/credit-transfer`, {
    method: 'POST',
    headers: { 'Idempotency-Key': input.idempotencyKey },
    body: JSON.stringify(body),
  })

  const [self, target] = await Promise.all([loadAccount(selfId), loadAccount(input.accountId)])
  const giving = input.direction === 'give'
  const amountMajor = paiseToDisplay(result.amount, parseCurrencyType(input.currencyType))
  const transfer: Transfer = {
    id: result.transferId,
    ref: result.transferId.slice(0, 12),
    at: new Date().toISOString(),
    fromId: giving ? self.id : target.id,
    toId: giving ? target.id : self.id,
    amount: amountMajor,
    direction: result.direction,
    remark: input.remark ?? '',
    balanceAfterFrom: giving ? self.balance : target.balance,
    balanceAfterTo: giving ? target.balance : self.balance,
    byId: self.id,
  }

  return {
    transfer,
    from: giving ? self : target,
    to: giving ? target : self,
    // The hierarchy service keeps no settlement position, so `agentNodeToAccount`
    // projects 0 for every account on the live path and this reads the same way
    // — one number, not a second story the rest of the console contradicts.
    settlementAfter: target.settlement,
  }
}

/** Give/Take — live hierarchy `POST …/credit-transfer`, or fixture when Demo. */
export async function transferCredit(input: api.TransferInput) {
  if (hasLiveApi()) return transferCreditLive(input)
  return api.transferCredit(input)
}

export function useTransferCredit() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: transferCredit,
    onSuccess: () => {
      // Own submit still invalidates immediately; the credit SSE also refreshes
      // for other tabs / concurrent operators on the same node.
      invalidateAfterCreditTransfer(client)
    },
  })
}
